/**
 * Historical U.S. benchmarks for backtesting and calibration.
 * All values are from published sources; used to compare model assumptions.
 * Citations and dates are included for scientific validity and telemetry.
 */

export const HISTORICAL_BENCHMARKS = {
    // -------------------------------------------------------------------------
    // Medicare / NHE cost growth (model uses ~5.1% from MedicareBaseline)
    // -------------------------------------------------------------------------
    medicareCostGrowth: {
        source: 'CMS Medicare Trustees Report, Table II.D1 (historical)',
        url: 'https://www.cms.gov/oact/tr',
        period: '2015-2024',
        observedAnnualGrowthPct: 5.2,
        modelAssumptionPct: 5.1,
        note: 'Model uses 5.1% extrapolation in MedicareBaseline.getCostPerBeneficiary.',
    },
    nheGrowthVsGdp: {
        source: 'CMS NHE Accounts, CBO Long-Term Outlook',
        period: '2010-2023',
        observedNheGrowthPct: 4.8,
        observedGdpGrowthPct: 2.3,
        modelNote: 'NHE baseline and GDP from nhe_baseline.json and federal_debt_baseline.',
    },

    // -------------------------------------------------------------------------
    // Tax gap / compliance (model default taxEvasionRate 12%)
    // -------------------------------------------------------------------------
    taxGap: {
        source: 'IRS Tax Gap Estimates (2020-2021)',
        url: 'https://www.irs.gov/newsroom/the-tax-gap',
        voluntaryCompliancePct: 85.0,
        impliedEvasionPct: 15.0,
        modelDefaultEvasionPct: 12.0,
        note: 'Model default 12% is slightly more optimistic than IRS headline gap.',
    },

    // -------------------------------------------------------------------------
    // Administrative share of health spending
    // -------------------------------------------------------------------------
    adminShare: {
        source: 'Woolhandler & Himmelstein (NEJM 2003), CMS NHE',
        medicareAdminPct: 2.0,
        privateInsurerAdminPct: 12.0,
        blendedUsPct: 15.0,
        modelScheduleTargetPct: 2.0,
        modelFloorPct: 4.5,
        note: 'Model uses 22%→2% schedule with 4.5% floor for multipayer realism.',
    },

    // -------------------------------------------------------------------------
    // Drug pricing (international vs US)
    // -------------------------------------------------------------------------
    drugPricesInternationalVsUs: {
        source: 'OECD Health Statistics, RAND prescription drug pricing reports',
        period: '2018-2023',
        internationalMedianAsPctOfUs: 35,
        modelDefaultInternationalPctOfUs: 40,
        note: 'Model default 40% is conservative (less savings) vs some OECD estimates.',
    },

    // -------------------------------------------------------------------------
    // Enrollment / uptake (ACA Medicaid expansion as proxy for ramp)
    // -------------------------------------------------------------------------
    enrollmentRamp: {
        source: 'ACA Medicaid expansion enrollment (KFF, CMS)',
        period: '2014-2016',
        year1TakeupPct: 60,
        year3TakeupPct: 85,
        modelPhase1EnrollmentPct: 72,
        modelPhase4EnrollmentPct: 100,
        note: 'Model Sec 1005 phase-in (72%, 85%, 98%, 100%) is broadly consistent with uptake curves.',
    },
};

/**
 * Get backtest comparison: model assumption vs historical benchmark.
 * Returns array of { module, metric, modelValue, benchmarkValue, unit, source, errorPct }.
 */
export function getBacktestComparisons() {
    const comparisons = [];

    const b = HISTORICAL_BENCHMARKS;

    comparisons.push({
        module: 'spending',
        metric: 'Medicare cost growth (annual %)',
        modelValue: b.medicareCostGrowth.modelAssumptionPct,
        benchmarkValue: b.medicareCostGrowth.observedAnnualGrowthPct,
        unit: 'percent',
        source: b.medicareCostGrowth.source,
        period: b.medicareCostGrowth.period,
        errorPct: Math.abs(b.medicareCostGrowth.modelAssumptionPct - b.medicareCostGrowth.observedAnnualGrowthPct) / b.medicareCostGrowth.observedAnnualGrowthPct * 100,
    });

    comparisons.push({
        module: 'revenue',
        metric: 'Tax evasion / non-compliance',
        modelValue: b.taxGap.modelDefaultEvasionPct,
        benchmarkValue: (100 - b.taxGap.voluntaryCompliancePct),
        unit: 'percent',
        source: b.taxGap.source,
        period: '2020-2021',
        errorPct: Math.abs(b.taxGap.modelDefaultEvasionPct - (100 - b.taxGap.voluntaryCompliancePct)) / (100 - b.taxGap.voluntaryCompliancePct) * 100,
    });

    // Admin: model floor is policy choice, not calibrated to current US; no numeric error.
    comparisons.push({
        module: 'spending',
        metric: 'Admin share (multipayer floor)',
        modelValue: b.adminShare.modelFloorPct,
        benchmarkValue: b.adminShare.blendedUsPct,
        unit: 'percent',
        source: b.adminShare.source,
        period: 'N/A',
        note: 'Floor is intentional multipayer realism, not fit to current US.',
        errorPct: null, // Not a calibration target
    });

    // Drug: both values already in percentage points (40 = 40%, 35 = 35%).
    const drugModelPct = b.drugPricesInternationalVsUs.modelDefaultInternationalPctOfUs;
    const drugBenchPct = b.drugPricesInternationalVsUs.internationalMedianAsPctOfUs;
    comparisons.push({
        module: 'spending',
        metric: 'International drug price (% of US)',
        modelValue: drugModelPct,
        benchmarkValue: drugBenchPct,
        unit: 'percent',
        source: b.drugPricesInternationalVsUs.source,
        period: b.drugPricesInternationalVsUs.period,
        errorPct: Math.abs(drugModelPct - drugBenchPct) / drugBenchPct * 100,
    });

    return comparisons;
}
